<!-- Tab bar -->
<div class="flex border-b border-slate-200 px-6" role="tablist">
  <button
    *ngFor="let tab of tabs"
    (click)="selectedTab = tab.id"
    role="tab"
    [attr.aria-selected]="selectedTab === tab.id"
    class="mr-6 border-b-2 py-3 text-sm font-medium transition-colors focus:outline-none"
    [class.border-violet-600]="selectedTab === tab.id"
    [class.text-violet-700]="selectedTab === tab.id"
    [class.border-transparent]="selectedTab !== tab.id"
    [class.text-slate-500]="selectedTab !== tab.id"
    [class.hover:text-slate-700]="selectedTab !== tab.id"
  >
    {{ tab.label }}
  </button>
</div>

<!-- ── Overview ───────────────────────────────────────────────────── -->
<div *ngIf="selectedTab === 'overview'" class="p-6" role="tabpanel">

  <div class="mb-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
    <div class="rounded-lg border border-slate-200 bg-slate-50 p-4">
      <p class="text-xs font-medium uppercase tracking-wide text-slate-400">Jobs</p>
      <p class="mt-1 text-2xl font-bold text-slate-900">{{ totalCount }}</p>
    </div>
    <div class="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
      <p class="text-xs font-medium uppercase tracking-wide text-emerald-500">Monitored</p>
      <p class="mt-1 text-2xl font-bold text-emerald-700">{{ monitoredCount }}</p>
    </div>
    <div class="rounded-lg border border-slate-200 bg-slate-50 p-4">
      <p class="text-xs font-medium uppercase tracking-wide text-slate-400">Succeeded</p>
      <p class="mt-1 text-2xl font-bold text-slate-900">{{ succeededCount }}</p>
    </div>
    <div class="rounded-lg border p-4"
         [class.border-red-200]="failedCount > 0"
         [class.bg-red-50]="failedCount > 0"
         [class.border-slate-200]="failedCount === 0"
         [class.bg-slate-50]="failedCount === 0">
      <p class="text-xs font-medium uppercase tracking-wide"
         [class.text-red-500]="failedCount > 0"
         [class.text-slate-400]="failedCount === 0">Failed</p>
      <p class="mt-1 text-2xl font-bold"
         [class.text-red-700]="failedCount > 0"
         [class.text-slate-900]="failedCount === 0">{{ failedCount }}</p>
    </div>
  </div>

</div>

<!-- ── Jobs ───────────────────────────────────────────────────────── -->
<div *ngIf="selectedTab === 'jobs'" role="tabpanel">

  <!-- Filter bar spans full width -->
  <app-cloud-filter-bar
    entityName="jobs"
    placeholder="Search job name…"
    [facets]="facets"
    [sortOptions]="sortOptions"
    [filters]="filters"
    [resultCount]="filteredJobs.length"
    [totalCount]="jobs.length"
    [quotaUsage]="quotaUsage"
    (filtersChange)="onFiltersChange($event)"
  ></app-cloud-filter-bar>

  <!-- Content row: table + optional run-history panel -->
  <div class="flex min-h-0" [class.divide-x]="selectedJob" [class.divide-slate-200]="selectedJob">

    <!-- Table area -->
    <div class="min-w-0 flex-1 p-6">

      <div *ngIf="loading" class="flex items-center gap-2 py-6 text-sm text-slate-400">
        <lucide-icon name="loader" [size]="16" class="animate-spin" [strokeWidth]="2"></lucide-icon>
        Loading jobs…
      </div>

      <div *ngIf="!loading" class="overflow-x-auto">
        <table class="w-full min-w-[660px] text-left text-sm">
          <thead>
            <tr class="border-b border-slate-200">
              <th class="pb-2.5 pr-4 text-xs font-semibold uppercase tracking-wide text-slate-400">Job Name</th>
              <th class="pb-2.5 pr-4 text-xs font-semibold uppercase tracking-wide text-slate-400">Type</th>
              <th class="pb-2.5 pr-4 text-xs font-semibold uppercase tracking-wide text-slate-400">Last Run</th>
              <th class="pb-2.5 pr-4 text-xs font-semibold uppercase tracking-wide text-slate-400">Duration</th>
              <th class="pb-2.5 pr-4 text-xs font-semibold uppercase tracking-wide text-slate-400">Status</th>
              <th class="pb-2.5 pr-4 text-xs font-semibold uppercase tracking-wide text-slate-400">Monitoring</th>
              <th class="pb-2.5 pr-4 text-xs font-semibold uppercase tracking-wide text-slate-400">Routing</th>
              <th class="pb-2.5 text-xs font-semibold uppercase tracking-wide text-slate-400"></th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let job of filteredJobs"
                class="border-b border-slate-100 last:border-0"
                [class.bg-violet-50]="job.name === selectedJob">
              <td class="py-3 pr-4 font-mono text-xs font-medium text-slate-900">{{ job.name }}</td>
              <td class="py-3 pr-4 text-xs text-slate-600">{{ job.type }}</td>
              <td class="py-3 pr-4 text-xs text-slate-600">{{ job.lastRunAt | cloudDate }}</td>
              <td class="py-3 pr-4 text-xs tabular-nums text-slate-600">{{ job.duration ?? '—' }}</td>
              <td class="py-3 pr-4">
                <span class="inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold"
                      [class]="statusClass(job.lastRunStatus)">
                  {{ job.lastRunStatus ?? '—' }}
                </span>
              </td>
              <!-- Monitoring -->
              <td class="py-3 pr-4 whitespace-nowrap">
                <ng-container *ngIf="isMonitored(job.name); else jobNotMonitored">
                  <button
                    type="button"
                    (click)="toggleMonitoring(job.name)"
                    class="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-700 transition-colors hover:bg-emerald-100 focus:outline-none focus:ring-2 focus:ring-emerald-300"
                    aria-label="Disable monitoring"
                  >
                    <span class="h-1.5 w-1.5 rounded-full bg-emerald-400"></span>
                    Monitored
                  </button>
                </ng-container>
                <ng-template #jobNotMonitored>
                  <ng-container *ngIf="canMonitorMore; else jobQuotaFull">
                    <button
                      type="button"
                      (click)="toggleMonitoring(job.name)"
                      class="rounded border border-slate-200 px-2 py-0.5 text-[11px] font-medium text-slate-500 transition-colors hover:border-slate-300 hover:bg-slate-50 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-violet-300"
                    >
                      Monitor
                    </button>
                  </ng-container>
                  <ng-template #jobQuotaFull>
                    <span class="group relative inline-block">
                      <button
                        type="button"
                        (click)="toggleMonitoring(job.name)"
                        aria-disabled="true"
                        class="cursor-not-allowed rounded border border-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-300 focus:outline-none"
                      >
                        Monitor
                      </button>
                      <span class="pointer-events-none absolute bottom-full left-1/2 z-10 mb-1.5 -translate-x-1/2 whitespace-nowrap rounded bg-slate-800 px-2 py-1 text-[10px] leading-tight text-white opacity-0 shadow-lg transition-opacity group-hover:opacity-100">
                        Quota reached
                      </span>
                    </span>
                  </ng-template>
                </ng-template>
              </td>
              <!-- Routing: override indicator + gear -->
              <td class="py-3 pr-4 whitespace-nowrap">
                <div class="flex items-center gap-1.5">
                  <span *ngIf="itemOverrideSummary(job.name)"
                        class="rounded-full border border-violet-200 bg-violet-50 px-1.5 py-0.5 text-[10px] font-medium text-violet-600">
                    {{ itemOverrideSummary(job.name) }}
                  </span>
                  <button
                    type="button"
                    (click)="openItemSettings(job.name)"
                    class="flex h-5 w-5 items-center justify-center rounded text-slate-300 transition-colors hover:bg-slate-100 hover:text-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-300"
                    [class.text-violet-500]="!!itemOverrideSummary(job.name)"
                    [attr.aria-label]="'Configure routing for ' + job.name"
                  >
                    <lucide-icon name="settings" [size]="12" [strokeWidth]="2"></lucide-icon>
                  </button>
                </div>
              </td>
              <td class="py-3 text-right">
                <button
                  (click)="job.name === selectedJob ? closeJob() : selectJob(job.name)"
                  class="rounded px-2 py-1 text-[11px] font-medium text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-violet-300"
                  [class.text-violet-600]="job.name === selectedJob"
                  [class.hover:text-violet-700]="job.name === selectedJob"
                >
                  {{ job.name === selectedJob ? 'Close' : 'View runs' }}
                </button>
              </td>
            </tr>
          </tbody>
        </table>

        <!-- Empty state: no matches -->
        <div *ngIf="filteredJobs.length === 0 && jobs.length > 0" class="py-10 text-center">
          <lucide-icon name="search" [size]="28" class="mx-auto mb-2 text-slate-300" [strokeWidth]="1.5"></lucide-icon>
          <p class="text-sm font-medium text-slate-500">No jobs match your filters</p>
          <p class="mt-0.5 text-xs text-slate-400">Try adjusting the search or clearing active filters.</p>
        </div>

        <!-- Empty state: no data -->
        <p *ngIf="jobs.length === 0" class="py-6 text-sm text-slate-400">
          No Glue jobs found in this account and region.
        </p>
      </div>

    </div>

    <!-- Run history panel -->
    <div *ngIf="selectedJob" class="flex w-96 shrink-0 flex-col">

      <!-- Panel header -->
      <div class="flex items-center justify-between border-b border-slate-200 px-4 py-3">
        <div class="flex min-w-0 items-center gap-2">
          <lucide-icon name="clock" [size]="14" class="shrink-0 text-slate-400" [strokeWidth]="2"></lucide-icon>
          <span class="truncate font-mono text-xs font-semibold text-slate-800">{{ selectedJob }}</span>
        </div>
        <button
          (click)="closeJob()"
          class="ml-2 shrink-0 rounded p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 focus:outline-none focus:ring-2 focus:ring-violet-300"
          aria-label="Close run history"
        >
          <lucide-icon name="x" [size]="14" [strokeWidth]="2"></lucide-icon>
        </button>
      </div>

      <p class="border-b border-slate-100 px-4 py-2 text-[11px] font-medium uppercase tracking-wide text-slate-400">
        Run History
      </p>

      <!-- Panel body -->
      <div class="flex-1 overflow-y-auto">

        <!-- Loading -->
        <div *ngIf="runsLoading" class="flex items-center gap-2 px-4 py-6 text-sm text-slate-400">
          <lucide-icon name="loader" [size]="14" class="animate-spin" [strokeWidth]="2"></lucide-icon>
          Loading…
        </div>

        <!-- Error -->
        <p *ngIf="runsError && !runsLoading" class="px-4 py-4 text-xs text-red-500">
          {{ runsError }}
        </p>

        <!-- Run list -->
        <div *ngIf="!runsLoading && !runsError">

          <div *ngIf="jobRuns.length === 0" class="px-4 py-6 text-sm text-slate-400">
            No runs found for this job.
          </div>

          <div *ngFor="let run of pagedJobRuns"
               class="border-b border-slate-100 px-4 py-3 last:border-0">

            <!-- Status + Run ID -->
            <div class="flex items-start justify-between gap-2">
              <span class="inline-flex shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold"
                    [class]="runStatusClass(run.status)">
                {{ run.status }}
              </span>
              <span class="truncate font-mono text-[10px] text-slate-400">{{ run.runId }}</span>
            </div>

            <!-- Timestamps + duration -->
            <div class="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-slate-500">
              <span *ngIf="run.startedAt">{{ run.startedAt | cloudDate }}</span>
              <span *ngIf="run.duration" class="tabular-nums text-slate-400">{{ run.duration }}</span>
            </div>

            <!-- Trigger -->
            <div *ngIf="run.trigger" class="mt-0.5 text-[11px] text-slate-400">
              {{ run.trigger }}
            </div>

            <!-- Error message -->
            <div *ngIf="run.errorMessage"
                 class="mt-1 rounded bg-red-50 px-2 py-1 text-[11px] text-red-600 line-clamp-2">
              {{ run.errorMessage }}
            </div>

          </div>

          <!-- Pagination -->
          <div *ngIf="runsTotalPages > 1"
               class="flex items-center justify-between border-t border-slate-100 px-4 py-2">
            <p class="text-[11px] text-slate-400">
              Page {{ runsPage }} of {{ runsTotalPages }}
            </p>
            <div class="flex items-center gap-1">
              <button
                (click)="runsPage = runsPage - 1"
                [disabled]="runsPage === 1"
                class="rounded px-2 py-1 text-[11px] font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-violet-300"
                [class.text-slate-300]="runsPage === 1"
                [class.cursor-not-allowed]="runsPage === 1"
                [class.text-slate-600]="runsPage !== 1"
                [class.hover:bg-slate-100]="runsPage !== 1"
              >Prev</button>
              <button
                (click)="runsPage = runsPage + 1"
                [disabled]="runsPage === runsTotalPages"
                class="rounded px-2 py-1 text-[11px] font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-violet-300"
                [class.text-slate-300]="runsPage === runsTotalPages"
                [class.cursor-not-allowed]="runsPage === runsTotalPages"
                [class.text-slate-600]="runsPage !== runsTotalPages"
                [class.hover:bg-slate-100]="runsPage !== runsTotalPages"
              >Next</button>
            </div>
          </div>

        </div>

      </div>
    </div>

  </div>

</div>

<!-- ── Activity ───────────────────────────────────────────────────── -->
<div *ngIf="selectedTab === 'activity'" role="tabpanel">
  <app-cloud-activity-feed
    [items]="activity"
    [loading]="activityLoading"
    serviceLabel="Glue"
    emptyHint="Recent job runs and monitoring findings (failures, timeouts) appear here."
    (itemSelect)="onActivitySelect($event)"
  ></app-cloud-activity-feed>
</div>

<!-- ── Item settings drawer ──────────────────────────────────────── -->
<app-item-settings-drawer
  [service]="serviceCard"
  [itemId]="drawerItemId"
  [open]="drawerItemId !== null"
  (closed)="closeItemSettings()"
></app-item-settings-drawer>
